




































import { Component, Prop, Vue, Watch } from 'vue-property-decorator'

@Component
export default class ModalFullImageViewer extends Vue {
  @Prop({ required: true }) id!: string
  @Prop({ default: '' }) src!: string
  @Prop({ default: false }) visible!: boolean

  private isRendered = false

  private scale = 1
  private tx = 0
  private ty = 0

  private lastScale = 1
  private lastDist = 0
  private lastMid = { x: 0, y: 0 }
  private isPinching = false

  private pinchStartTx = 0
  private pinchStartTy = 0
  private pinchStartMid = { x: 0, y: 0 }

  private isPanning = false
  private lastPan = { x: 0, y: 0 }
  private lastPanTime = 0
  private vx = 0
  private vy = 0
  private rafId: number | null = null

  private lastTapTime = 0
  private animating = false

  @Watch('src')
  onSrcChange() {
    this.reset(false)
  }

  @Watch('visible', { immediate: true })
  onVisibleChange(next: boolean) {
    if (next) {
      this.isRendered = true
      this.$nextTick(() => {
        this.$bvModal.show(this.id)
      })
      return
    }

    if (this.isRendered) {
      this.$bvModal.hide(this.id)
    }
  }

  get imgStyle() {
    return {
      transform: `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`,
      transformOrigin: 'center center',
      transition: this.animating ? 'transform 0.25s ease' : 'none',
    }
  }

  onHide() {
    this.reset(false)
  }

  onHidden() {
    this.$emit('update:visible', false)
    this.$emit('close')
    this.isRendered = false
  }

  beforeDestroy() {
    this.stopMomentum()
  }

  reset(animated = true) {
    this.stopMomentum()
    this.animating = animated
    this.scale = 1
    this.tx = 0
    this.ty = 0
    this.isPinching = false
    this.isPanning = false
    if (animated) setTimeout(() => { this.animating = false }, 260)
  }

  toggleZoom(focalX?: number, focalY?: number) {
    this.animating = true
    if (this.scale > 1) {
      this.scale = 1
      this.tx = 0
      this.ty = 0
    } else {
      const newScale = 2.5
      this.scale = newScale
      if (focalX !== undefined && focalY !== undefined) {
        const wrapper = (this.$refs.img as HTMLImageElement | undefined)?.parentElement
        const rect = wrapper?.getBoundingClientRect()
        const wrapCx = rect ? rect.left + rect.width / 2 : window.innerWidth / 2
        const wrapCy = rect ? rect.top + rect.height / 2 : window.innerHeight / 2
        this.tx = (focalX - wrapCx) * (1 - newScale)
        this.ty = (focalY - wrapCy) * (1 - newScale)
        this.clamp()
      }
    }
    setTimeout(() => { this.animating = false }, 260)
  }

  onDblClick(e: MouseEvent) {
    this.toggleZoom(e.clientX, e.clientY)
  }

  handleBackdropClick() {
    if (this.scale > 1) {
      this.reset(true)
    } else {
      this.$bvModal.hide(this.id)
    }
  }

  private getDist(touches: TouchList): number {
    return Math.hypot(
      touches[0].clientX - touches[1].clientX,
      touches[0].clientY - touches[1].clientY
    )
  }

  private getMid(touches: TouchList) {
    return {
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2,
    }
  }

  onTouchStart(e: TouchEvent) {
    this.stopMomentum()
    this.animating = false
    if (e.touches.length === 2) {
      this.isPinching = true
      this.isPanning = false
      this.lastDist = this.getDist(e.touches)
      this.lastMid = this.getMid(e.touches)
      this.lastScale = this.scale
      this.pinchStartTx = this.tx
      this.pinchStartTy = this.ty
      this.pinchStartMid = { ...this.lastMid }
    } else if (e.touches.length === 1) {
      const now = Date.now()
      if (now - this.lastTapTime < 280) {
        this.toggleZoom(e.touches[0].clientX, e.touches[0].clientY)
        this.lastTapTime = 0
        return
      }
      this.lastTapTime = now
      this.isPanning = this.scale > 1
      this.isPinching = false
      this.lastPan = { x: e.touches[0].clientX, y: e.touches[0].clientY }
      this.vx = 0
      this.vy = 0
      this.lastPanTime = now
    }
  }

  onTouchMove(e: TouchEvent) {
    if (e.touches.length === 2 && this.isPinching) {
      const d = this.getDist(e.touches)
      if (this.lastDist === 0) { this.lastDist = d; return }

      const newScale = Math.min(5, Math.max(1, this.lastScale * (d / this.lastDist)))
      const scaleRatio = newScale / this.lastScale

      const m = this.getMid(e.touches)

      const wrapper = (this.$refs.img as HTMLImageElement | undefined)?.parentElement
      const rect = wrapper?.getBoundingClientRect()
      const wrapCx = rect ? rect.left + rect.width / 2 : window.innerWidth / 2
      const wrapCy = rect ? rect.top + rect.height / 2 : window.innerHeight / 2

      const focalX = this.pinchStartMid.x - wrapCx
      const focalY = this.pinchStartMid.y - wrapCy

      this.scale = newScale
      this.tx = focalX * (1 - scaleRatio) + this.pinchStartTx * scaleRatio + (m.x - this.pinchStartMid.x)
      this.ty = focalY * (1 - scaleRatio) + this.pinchStartTy * scaleRatio + (m.y - this.pinchStartMid.y)
      this.clamp()
    } else if (e.touches.length === 1 && this.isPanning) {
      const now = Date.now()
      const dt = now - this.lastPanTime
      const dx = e.touches[0].clientX - this.lastPan.x
      const dy = e.touches[0].clientY - this.lastPan.y
      if (dt > 0) {
        this.vx = dx / dt
        this.vy = dy / dt
      }
      this.tx += dx
      this.ty += dy
      this.lastPan = { x: e.touches[0].clientX, y: e.touches[0].clientY }
      this.lastPanTime = now
      this.clamp()
    }
  }

  onTouchEnd(e: TouchEvent) {
    if (e.touches.length < 2 && this.isPinching) {
      this.isPinching = false
      if (e.touches.length === 1 && this.scale > 1) {
        this.isPanning = true
        this.lastPan = { x: e.touches[0].clientX, y: e.touches[0].clientY }
        this.vx = 0
        this.vy = 0
        this.lastPanTime = Date.now()
      }
    }
    if (e.touches.length === 0) {
      this.isPanning = false
      if (this.scale > 1 && (Math.abs(this.vx) > 0.1 || Math.abs(this.vy) > 0.1)) {
        this.startMomentum()
      }
    }
    if (this.scale < 1) this.reset(true)
  }

  private startMomentum() {
    const friction = 0.95
    const tick = () => {
      this.vx *= friction
      this.vy *= friction
      const nextTx = this.tx + this.vx * 16
      const nextTy = this.ty + this.vy * 16
      this.tx = nextTx
      this.ty = nextTy
      this.clamp()
      if (Math.abs(this.tx - nextTx) > 0.5) this.vx = 0
      if (Math.abs(this.ty - nextTy) > 0.5) this.vy = 0
      if (Math.abs(this.vx) > 0.01 || Math.abs(this.vy) > 0.01) {
        this.rafId = requestAnimationFrame(tick)
      } else {
        this.rafId = null
      }
    }
    this.rafId = requestAnimationFrame(tick)
  }

  private stopMomentum() {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
    this.vx = 0
    this.vy = 0
  }

  private clamp() {
    const el = this.$refs.img as HTMLImageElement | undefined
    const wrapper = el?.parentElement
    if (el && wrapper) {
      const maxTx = Math.max(0, (el.offsetWidth * this.scale - wrapper.offsetWidth) / 2)
      const maxTy = Math.max(0, (el.offsetHeight * this.scale - wrapper.offsetHeight) / 2)
      this.tx = Math.max(-maxTx, Math.min(maxTx, this.tx))
      this.ty = Math.max(-maxTy, Math.min(maxTy, this.ty))
    } else {
      const limit = (this.scale - 1) * 160
      this.tx = Math.max(-limit, Math.min(limit, this.tx))
      this.ty = Math.max(-limit, Math.min(limit, this.ty))
    }
  }
}
